import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Paper,
  IconButton,
  FormControlLabel,
  Switch,
  Divider,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  Code as CodeIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import MeasurementCroqui from './MeasurementCroqui';

const authHeaders = (extra = {}) => {
  const token = localStorage.getItem('token');
  return {
    ...extra,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const PatternUpload = ({ brandId, userId, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    category: 'Tops',
    description: '',
    pieces: [],
    pieceCount: 1,
    measurements: [],
    tags: [],
    color: '#2196f3',
    seamAllowance: 1.0,
    difficulty: 'intermediate',
    fabricTypes: [],
    grainLine: 'vertical',
    isPublic: false,
    available: true,
    comingSoon: false,
    status: 'draft',
    version: '1.0.0',
  });

  const [files, setFiles] = useState({
    image: null,
    svgTemplate: null,
    yamlTemplate: null,
  });

  const [previews, setPreviews] = useState({
    image: null,
  });

  const [pieceInput, setPieceInput] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const CATEGORIES = ['Tops', 'Bottoms', 'Dresses', 'Outerwear', 'Accessories'];
  const FABRIC_TYPES = ['woven', 'knit', 'stretch', 'non-stretch'];
  const DIFFICULTY_LEVELS = ['beginner', 'intermediate', 'advanced', 'expert'];
  const GRAIN_LINES = ['vertical', 'horizontal', 'bias'];

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    setFiles((prev) => ({
      ...prev,
      [fieldName]: file,
    }));

    // Create preview for images
    if (fieldName === 'image' && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviews((prev) => ({
          ...prev,
          image: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddPiece = () => {
    if (pieceInput.trim()) {
      setFormData((prev) => ({
        ...prev,
        pieces: [...prev.pieces, pieceInput.trim()],
        pieceCount: prev.pieces.length + 1,
      }));
      setPieceInput('');
    }
  };

  const handleRemovePiece = (index) => {
    setFormData((prev) => ({
      ...prev,
      pieces: prev.pieces.filter((_, i) => i !== index),
      pieceCount: prev.pieces.length - 1,
    }));
  };

  const handleAddTag = () => {
    if (tagInput.trim()) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim().toLowerCase()],
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (index) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index),
    }));
  };

  const handleMeasurementToggle = (measurementKey) => {
    setFormData((prev) => {
      const measurements = prev.measurements.includes(measurementKey)
        ? prev.measurements.filter((m) => m !== measurementKey)
        : [...prev.measurements, measurementKey];
      return { ...prev, measurements };
    });
  };

  const handleFabricTypeChange = (fabricType) => {
    setFormData((prev) => {
      const fabricTypes = prev.fabricTypes.includes(fabricType)
        ? prev.fabricTypes.filter((t) => t !== fabricType)
        : [...prev.fabricTypes, fabricType];
      return { ...prev, fabricTypes };
    });
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error('Pattern name is required');
      return false;
    }

    if (!formData.description.trim()) {
      toast.error('Pattern description is required');
      return false;
    }

    if (formData.pieces.length === 0) {
      toast.error('At least one pattern piece is required');
      return false;
    }

    if (!files.image) {
      toast.error('Pattern preview image is required');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    const loadingToast = toast.loading('Creating pattern...');

    try {
      // Step 1: Upload image first to get the URL
      let imageUrl = '';
      if (files.image) {
        const formDataUpload = new FormData();
        formDataUpload.append('image', files.image);

        // For now, we'll create a temporary URL
        // In production, this would upload to S3 or similar
        imageUrl = URL.createObjectURL(files.image);
      }

      // Step 2: Create the pattern
      const patternData = {
        ...formData,
        brandId: brandId || null,
        createdBy: userId,
        imageUrl: imageUrl,
        specifications: {
          seamAllowance: formData.seamAllowance,
          grainLine: formData.grainLine,
          fabricType: formData.fabricTypes,
          difficulty: formData.difficulty,
        },
      };

      const response = await fetch('/api/patterns', {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(patternData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create pattern');
      }

      const result = await response.json();
      const patternId = result.pattern._id;

      // Step 3: Upload files if they exist
      if (files.image || files.svgTemplate || files.yamlTemplate) {
        const uploadFormData = new FormData();

        if (files.image) {
          uploadFormData.append('image', files.image);
        }
        if (files.svgTemplate) {
          uploadFormData.append('svgTemplate', files.svgTemplate);
        }
        if (files.yamlTemplate) {
          uploadFormData.append('yamlTemplate', files.yamlTemplate);
        }

        const uploadResponse = await fetch(`/api/patterns/${patternId}/upload`, {
          method: 'POST',
          headers: authHeaders(),
          body: uploadFormData,
        });

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload pattern files');
        }
      }

      toast.success('Pattern created successfully!', { id: loadingToast });

      if (onSuccess) {
        onSuccess(result.pattern);
      }
    } catch (error) {
      console.error('Error creating pattern:', error);
      toast.error(error.message || 'Failed to create pattern', { id: loadingToast });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Basic Information
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Pattern Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Crew Neck T-Shirt"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    label="Category"
                  >
                    {CATEGORIES.map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {cat}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  multiline
                  rows={3}
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Describe the pattern and its features..."
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Version"
                  name="version"
                  value={formData.version}
                  onChange={handleInputChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Color (Hex)"
                  name="color"
                  value={formData.color}
                  onChange={handleInputChange}
                  type="color"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Pattern Pieces */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Pattern Pieces
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                label="Add Pattern Piece"
                value={pieceInput}
                onChange={(e) => setPieceInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddPiece())}
                placeholder="e.g., Front, Back, Sleeve"
              />
              <Button variant="outlined" onClick={handleAddPiece}>
                Add
              </Button>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
              {formData.pieces.map((piece, index) => (
                <Chip
                  key={index}
                  label={piece}
                  onDelete={() => handleRemovePiece(index)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>

            {formData.pieces.length === 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Add at least one pattern piece
              </Alert>
            )}
          </Paper>
        </Grid>

        {/* Measurements */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <MeasurementCroqui
              selectedMeasurements={formData.measurements}
              onMeasurementToggle={handleMeasurementToggle}
            />
          </Paper>
        </Grid>

        {/* Specifications */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Pattern Specifications
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Seam Allowance (cm)"
                  name="seamAllowance"
                  value={formData.seamAllowance}
                  onChange={handleInputChange}
                  inputProps={{ step: 0.1, min: 0 }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Grain Line</InputLabel>
                  <Select
                    name="grainLine"
                    value={formData.grainLine}
                    onChange={handleInputChange}
                    label="Grain Line"
                  >
                    {GRAIN_LINES.map((gl) => (
                      <MenuItem key={gl} value={gl}>
                        {gl.charAt(0).toUpperCase() + gl.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Difficulty</InputLabel>
                  <Select
                    name="difficulty"
                    value={formData.difficulty}
                    onChange={handleInputChange}
                    label="Difficulty"
                  >
                    {DIFFICULTY_LEVELS.map((level) => (
                      <MenuItem key={level} value={level}>
                        {level.charAt(0).toUpperCase() + level.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Fabric Types
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {FABRIC_TYPES.map((type) => (
                    <Chip
                      key={type}
                      label={type.charAt(0).toUpperCase() + type.slice(1)}
                      onClick={() => handleFabricTypeChange(type)}
                      color={formData.fabricTypes.includes(type) ? 'primary' : 'default'}
                      variant={formData.fabricTypes.includes(type) ? 'filled' : 'outlined'}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* File Uploads */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Pattern Files
            </Typography>

            {/* Image Upload */}
            <Card variant="outlined" sx={{ mb: 2, mt: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ImageIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Preview Image *
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadIcon />}
                    size="small"
                  >
                    Choose File
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, 'image')}
                    />
                  </Button>
                </Box>

                {previews.image && (
                  <Box sx={{ mt: 2 }}>
                    <img
                      src={previews.image}
                      alt="Pattern preview"
                      style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8 }}
                    />
                  </Box>
                )}

                {files.image && (
                  <Chip
                    label={files.image.name}
                    onDelete={() => {
                      setFiles((prev) => ({ ...prev, image: null }));
                      setPreviews((prev) => ({ ...prev, image: null }));
                    }}
                    color="success"
                    sx={{ mt: 1 }}
                  />
                )}
              </CardContent>
            </Card>

            {/* SVG Template */}
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CodeIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight={600}>
                      SVG Template (Optional)
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadIcon />}
                    size="small"
                  >
                    Choose File
                    <input
                      type="file"
                      hidden
                      accept=".svg"
                      onChange={(e) => handleFileChange(e, 'svgTemplate')}
                    />
                  </Button>
                </Box>

                {files.svgTemplate && (
                  <Chip
                    label={files.svgTemplate.name}
                    onDelete={() => setFiles((prev) => ({ ...prev, svgTemplate: null }))}
                    color="success"
                    sx={{ mt: 1 }}
                  />
                )}
              </CardContent>
            </Card>

            {/* YAML Template */}
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CodeIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight={600}>
                      YAML Template (Optional)
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadIcon />}
                    size="small"
                  >
                    Choose File
                    <input
                      type="file"
                      hidden
                      accept=".yaml,.yml"
                      onChange={(e) => handleFileChange(e, 'yamlTemplate')}
                    />
                  </Button>
                </Box>

                {files.yamlTemplate && (
                  <Chip
                    label={files.yamlTemplate.name}
                    onDelete={() => setFiles((prev) => ({ ...prev, yamlTemplate: null }))}
                    color="success"
                    sx={{ mt: 1 }}
                  />
                )}
              </CardContent>
            </Card>
          </Paper>
        </Grid>

        {/* Tags */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Tags
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                label="Add Tag"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                placeholder="e.g., basic, summer, casual"
              />
              <Button variant="outlined" onClick={handleAddTag}>
                Add
              </Button>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
              {formData.tags.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  onDelete={() => handleRemoveTag(index)}
                  variant="outlined"
                />
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Status & Visibility */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Status & Visibility
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    label="Status"
                  >
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="published">Published</MenuItem>
                    <MenuItem value="archived">Archived</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.available}
                      onChange={handleInputChange}
                      name="available"
                    />
                  }
                  label="Available"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.comingSoon}
                      onChange={handleInputChange}
                      name="comingSoon"
                    />
                  }
                  label="Coming Soon"
                />
              </Grid>

              {!brandId && (
                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isPublic}
                        onChange={handleInputChange}
                        name="isPublic"
                      />
                    }
                    label="Public Pattern"
                  />
                </Grid>
              )}
            </Grid>

            {!brandId && formData.isPublic && (
              <Alert severity="info" sx={{ mt: 2 }}>
                This pattern will be available to all brands as a global pattern.
              </Alert>
            )}
          </Paper>
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            {onCancel && (
              <Button
                variant="outlined"
                onClick={onCancel}
                disabled={submitting}
                size="large"
              >
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              size="large"
              startIcon={submitting ? null : <CheckIcon />}
            >
              {submitting ? 'Creating Pattern...' : 'Create Pattern'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PatternUpload;
